import { Head, router } from '@inertiajs/react'
import { useEffect } from 'react'
import styles from '@/pages/pages/landing.module.css'

const WHATSAPP_NUMBER = '33760169588'
const DEMO_MESSAGE = 'Bonjour, je souhaite réserver une démo de Seamflow.'
const CONTACT_MESSAGE = "Bonjour, j'aimerais en savoir plus sur Seamflow."

const whatsAppLink = (message: string) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`

const NAV_LINKS = [
  { label: 'Usages', href: '#modes' },
  { label: 'Traçabilité', href: '#tracabilite' },
  { label: 'Fonctionnalités', href: '#fonctions' },
  { label: 'Équipe', href: '#roles' },
  { label: 'Formation', href: '#formation' },
  { label: 'Tarif', href: '#tarif' },
]

const MODES = [
  {
    icon: '🏠',
    title: 'Atelier indépendant',
    desc: "Gérez votre atelier en autonomie totale : commandes, suivi des articles, encaissement, facturation et récapitulatifs. Aucune boutique partenaire requise pour démarrer.",
  },
  {
    icon: '🔗',
    title: 'Boutiques connectées',
    desc: 'Reliez vos boutiques partenaires à votre atelier. Un retailer peut travailler avec plusieurs retoucheurs, un retoucheur peut servir plusieurs boutiques. Chaque flux reste cloisonné.',
  },
  {
    icon: '🤝',
    title: 'B2B & B2C réunis',
    desc: 'Servez le client final au comptoir et la société partenaire depuis la même interface. Tarification, suivi et facturation distincts, automatiquement appliqués selon le type de client.',
  },
]

const TRACE_STEPS = [
  { icon: '📝', role: 'Enregistré', action: 'La commande est créée et collectée.', by: '— Anthony · 12/06 17:23' },
  { icon: '✂️', role: 'Travaillé', action: 'Le retoucheur réalise la prestation.', by: '— Cyril · 16/06 13:34' },
  { icon: '✅', role: 'Vérifié', action: 'Contrôle qualité avant remise.', by: '— Cyril · 16/06 13:29' },
  { icon: '🚚', role: 'Livré', action: 'Remis au client ou à la société.', by: '— Anthony · 16/06 14:37' },
  { icon: '💳', role: 'Encaissé', action: 'Paiement enregistré et attribué.', by: '— Sarah · 16/06 · espèces' },
]

const ROLES = [
  {
    iconClass: 'accueil',
    icon: '🛎️',
    title: "Agent d'accueil",
    desc: 'Crée les commandes au comptoir, gère les clients, encaisse et imprime les bons de dépôt.',
    tags: ['Comptoir & encaissement', 'Clients & devis'],
  },
  {
    iconClass: 'couturier',
    icon: '🪡',
    title: 'Couturier',
    desc: 'Voit les commandes à réaliser, marque les travaux terminés, vérifie la qualité avant remise.',
    tags: ['Production & à faire', 'Vérification qualité'],
  },
  {
    iconClass: 'coursier',
    icon: '🚚',
    title: 'Coursier',
    desc: 'Consulte ses tournées par zone de livraison, la liste des boutiques à desservir et confirme les remises.',
    tags: ['Tournées & zones', 'Collectes & livraisons'],
  },
]

const TESTIMONIALS = [
  {
    initials: 'KM',
    text: "On ne perd plus aucune commande entre la boutique et l'atelier. Savoir qui a fait quoi, à quelle heure, ça a changé notre quotidien — et celui de nos clients.",
    name: 'Kamel Megueni Tani',
    role: 'Gérant · Stanil / Cordonnerie Venture · Marseille',
  },
  {
    initials: 'JC',
    text: "Le récapitulatif mensuel automatique nous fait gagner des heures. Avant c'était des tableaux Excel approximatifs. Maintenant tout est là, propre, en un PDF.",
    name: 'Josy Couture',
    role: 'Atelier de retouche · Septèmes-les-Vallons',
  },
]

const PRINTER_HIGHLIGHT = {
  icon: '🖨️',
  title: 'Imprimante Star Micronics fournie',
  desc: 'Mise à disposition incluse — impression auto via CloudPRNT.',
  tag: 'Incluse',
}

const PRICE_PLANS = [
  {
    alt: false,
    type: 'Pour les ateliers de retouche',
    badge: 'Tout inclus · le plus complet',
    price: '79',
    period: 'HT / mois / boutique · engagement 12 mois',
    features: [
      'Plateforme complète',
      'Mode autonome ou connecté',
      'Multi-boutiques & retoucheurs',
      'Comptes accueil / couturier / coursier',
      'Traçabilité article complète',
      'Z de caisse & récap. sociétés',
      'Facturation électronique',
      'Catalogue double tarif B2B / B2C',
      'Interface de livraison & tournées',
      'Accès web illimité tous appareils',
    ],
    highlights: [
      PRINTER_HIGHLIGHT,
      {
        icon: '💬',
        title: 'Envoi des SMS clients inclus',
        desc: 'À volonté — aucun coût au message, aucun forfait à gérer.',
        tag: 'Inclus',
      },
    ],
  },
  {
    alt: true,
    type: 'Pour les boutiques / retailers',
    badge: 'Confiez vos retouches',
    price: '59',
    period: 'HT / mois / boutique · engagement 12 mois',
    features: [
      'Connexion à plusieurs retoucheurs',
      'Traçabilité article complète',
      'Comptes accueil',
      'Suivi de facturation',
      'Accès web illimité tous appareils',
    ],
    highlights: [
      PRINTER_HIGHLIGHT,
      {
        icon: '💬',
        title: 'Envoi des SMS inclus',
        desc: 'À volonté — aucun coût au message, aucun forfait à gérer.',
        tag: 'Inclus',
      },
    ],
  },
]

export default function LandingPage() {
  const cx = (...names: string[]) => names.map((name) => styles[name]).filter(Boolean).join(' ')
  const goToLogin = () => router.visit('/session/new')

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add(styles.visible)
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    )
    document.querySelectorAll(`.${styles.reveal}`).forEach((element) => observer.observe(element))
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <Head>
        <title>Seamflow — Le logiciel de gestion des ateliers de retouche</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap"
          rel="stylesheet"
        />
      </Head>

      <div className={styles.landing}>
        {/* ============ NAV ============ */}
        <nav className={styles.nav}>
          <div className={cx('nav-logo')}>Seamflow</div>
          <ul className={cx('nav-links')}>
            {NAV_LINKS.map((link) => (
              <li key={link.label}>
                <a href={link.href}>{link.label}</a>
              </li>
            ))}
          </ul>
          <div className={cx('nav-right')}>
            <button className={cx('btn-connect')} onClick={goToLogin}>
              Se connecter
            </button>
          </div>
        </nav>

        {/* ============ HERO ============ */}
        <section className={styles.hero}>
          <div className={cx('hero-badge')}>✨ La retouche, enfin modernisée</div>
          <h1 className={cx('hero-title')}>
            Gérez vos retouches
            <span className={cx('brand')}>sans prise de tête</span>
          </h1>
          <p className={cx('hero-sub')}>
            De la boutique à l'atelier, chaque article suivi en temps réel. Seul ou connecté à vos boutiques
            partenaires — Seamflow s'adapte à votre organisation, en B2B comme en B2C.
          </p>
          <div className={cx('hero-actions')}>
            <a className={cx('btn-primary')} href={whatsAppLink(DEMO_MESSAGE)} target="_blank" rel="noopener noreferrer">
              Réserver une démo →
            </a>
            <button className={cx('btn-outline')} onClick={goToLogin}>
              Se connecter
            </button>
          </div>
          <div className={cx('hero-note')}>
            Déjà utilisé quotidiennement par des ateliers et boutiques à Marseille.
          </div>

          {/* App preview — sidebar fidèle */}
          <div className={cx('hero-preview')}>
            <div className={cx('preview-bar')}>
              <div className={cx('dot', 'dot-r')} />
              <div className={cx('dot', 'dot-y')} />
              <div className={cx('dot', 'dot-g')} />
              <div className={cx('preview-url')}>seamflow.fr</div>
            </div>
            <div className={cx('preview-body')}>
              <div className={cx('preview-sidebar')}>
                <div className={cx('preview-sidebar-org')}>Josy Couture</div>
                <div className={cx('preview-sidebar-group')}>Particuliers</div>
                <div className={cx('preview-sidebar-item')}>Comptoir</div>
                <div className={cx('preview-sidebar-item')}>Clients</div>
                <div className={cx('preview-sidebar-group')}>Sociétés</div>
                <div className={cx('preview-sidebar-item')}>Comptoir</div>
                <div className={cx('preview-sidebar-item')}>Contrats</div>
                <div className={cx('preview-sidebar-group')}>Production</div>
                <div className={cx('preview-sidebar-item', 'active')}>Commandes</div>
                <div className={cx('preview-sidebar-item')}>Envoi SMS</div>
                <div className={cx('preview-sidebar-group')}>Finance</div>
                <div className={cx('preview-sidebar-item')}>Z de Caisse</div>
                <div className={cx('preview-sidebar-item')}>Récap. Sociétés</div>
                <div className={cx('preview-sidebar-group')}>Configuration</div>
                <div className={cx('preview-sidebar-item')}>Catalogue</div>
                <div className={cx('preview-sidebar-item')}>Autorisations</div>
                <div className={cx('preview-sidebar-item')}>Abonnement</div>
              </div>
              <div className={cx('preview-main')}>
                <div className={cx('preview-main-title')}>Commandes</div>
                <div className={cx('preview-stats-row')}>
                  <div className={cx('preview-stat')}>
                    <div className={cx('preview-stat-icon')}>👕</div>
                    <div className={cx('preview-stat-num')}>6 122</div>
                    <div className={cx('preview-stat-label')}>Articles</div>
                  </div>
                  <div className={cx('preview-stat')}>
                    <div className={cx('preview-stat-icon')}>✂️</div>
                    <div className={cx('preview-stat-num')}>7 184</div>
                    <div className={cx('preview-stat-label')}>Services</div>
                  </div>
                  <div className={cx('preview-stat')}>
                    <div className={cx('preview-stat-icon')}>⏱️</div>
                    <div className={cx('preview-stat-num')}>1 086</div>
                    <div className={cx('preview-stat-label')}>À faire</div>
                  </div>
                  <div className={cx('preview-stat')}>
                    <div className={cx('preview-stat-icon')}>✅</div>
                    <div className={cx('preview-stat-num')}>6 098</div>
                    <div className={cx('preview-stat-label')}>Faits</div>
                  </div>
                </div>
                <div className={cx('preview-table')}>
                  <div className={cx('preview-table-row', 'header')}>
                    <span>N°</span>
                    <span>Client</span>
                    <span>Article</span>
                    <span>Service</span>
                    <span>Statut</span>
                  </div>
                  <div className={cx('preview-table-row')}>
                    <span>#4625</span>
                    <span>Nicole R.</span>
                    <span>Jeans</span>
                    <span>
                      <span className={cx('spill', 'spill-svc')}>Formule côtés + OBR</span>
                    </span>
                    <span>
                      <span className={cx('spill', 'spill-todo')}>À faire</span>
                    </span>
                  </div>
                  <div className={cx('preview-table-row')}>
                    <span>#4621</span>
                    <span>Reda B.</span>
                    <span>Pantalon</span>
                    <span>
                      <span className={cx('spill', 'spill-svc')}>Ourlet simple</span>
                    </span>
                    <span>
                      <span className={cx('spill', 'spill-todo')}>À faire</span>
                    </span>
                  </div>
                  <div className={cx('preview-table-row')}>
                    <span>#4611</span>
                    <span>Rose A.</span>
                    <span>Jupe / Robe</span>
                    <span>
                      <span className={cx('spill', 'spill-svc')}>Ourlet piqué</span>
                    </span>
                    <span>
                      <span className={cx('spill', 'spill-todo')}>À faire</span>
                    </span>
                  </div>
                  <div className={cx('preview-table-row')}>
                    <span>#4610</span>
                    <span>Jaqueline R.</span>
                    <span>Pantalon</span>
                    <span>
                      <span className={cx('spill', 'spill-svc')}>Ourlet revers large</span>
                    </span>
                    <span>
                      <span className={cx('spill', 'spill-todo')}>À faire</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ STATS BAND ============ */}
        <section className={styles['stats-band']}>
          <div className={cx('stats-band-label-top', 'reveal')}>
            Des chiffres d'un atelier qui tourne, pas d'une démo
          </div>
          <div className={cx('stats-band-inner')}>
            <div className={cx('reveal')}>
              <div className={cx('stat-big')}>
                <span className={cx('accent')}>6 122</span>
              </div>
              <div className={cx('stat-big-label')}>articles traités</div>
            </div>
            <div className={cx('reveal')}>
              <div className={cx('stat-big')}>
                <span className={cx('accent')}>7 184</span>
              </div>
              <div className={cx('stat-big-label')}>services réalisés</div>
            </div>
            <div className={cx('reveal')}>
              <div className={cx('stat-big')}>
                <span className={cx('accent')}>6 098</span>
              </div>
              <div className={cx('stat-big-label')}>commandes livrées</div>
            </div>
            <div className={cx('reveal')}>
              <div className={cx('stat-big')}>
                <span className={cx('accent')}>100%</span>
              </div>
              <div className={cx('stat-big-label')}>tracées de bout en bout</div>
            </div>
          </div>
        </section>

        {/* ============ MODES ============ */}
        <section className={styles.modes} id="modes">
          <div className={cx('modes-inner')}>
            <div className={cx('modes-header', 'reveal')}>
              <span className={cx('section-eyebrow')}>Flexible par conception</span>
              <h2 className={cx('section-title')}>
                Seul ou connecté,
                <br />
                Seamflow s'adapte.
              </h2>
              <p className={cx('section-sub')}>
                Atelier indépendant, retailer multi-boutiques, ou les deux à la fois. La plateforme gère le particulier
                au comptoir comme la société partenaire, sans changer d'outil.
              </p>
            </div>
            <div className={cx('modes-grid')}>
              {MODES.map((mode) => (
                <div key={mode.title} className={cx('mode-card', 'reveal')}>
                  <div className={cx('mode-icon')}>{mode.icon}</div>
                  <div className={cx('mode-title')}>{mode.title}</div>
                  <p className={cx('mode-desc')}>{mode.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ TRACING ============ */}
        <section className={styles.tracing} id="tracabilite">
          <div className={cx('tracing-inner')}>
            <div className={cx('tracing-header', 'reveal')}>
              <span className={cx('section-eyebrow', 'section-eyebrow-dark')}>Traçabilité complète</span>
              <h2 className={cx('section-title')}>Chaque article a son histoire.</h2>
              <p className={cx('section-sub')}>
                Du dépôt à l'encaissement, aucune étape n'est anonyme. Seamflow enregistre qui a fait quoi, et à quel
                moment — horodaté, nominatif, infalsifiable.
              </p>
            </div>
            <div className={cx('trace-timeline', 'reveal')}>
              {TRACE_STEPS.map((step) => (
                <div key={step.role} className={cx('trace-step')}>
                  <div className={cx('trace-dot')}>{step.icon}</div>
                  <div className={cx('trace-role')}>{step.role}</div>
                  <div className={cx('trace-action')}>{step.action}</div>
                  <div className={cx('trace-by')}>{step.by}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ FEATURE: COMMANDES + PLANNING ============ */}
        <section className={cx('feature-block')} id="fonctions">
          <div className={cx('feature-inner')}>
            <div className={cx('feature-text', 'reveal')}>
              <span className={cx('section-eyebrow')}>Production</span>
              <h2 className={cx('section-title')}>
                Votre planning du jour,
                <br />
                en un filtre.
              </h2>
              <p className={cx('section-sub')}>
                Filtrez par date de remise et obtenez instantanément la charge de travail de la journée : articles,
                services, ce qu'il reste à faire et ce qui est terminé.
              </p>
              <ul className={cx('feature-list')}>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Filtres puissants</div>
                    <div className={cx('feature-li-desc')}>
                      Par date de remise, statut, type de client, ou recherche libre sur client / article / service.
                    </div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Récapitulatif instantané</div>
                    <div className={cx('feature-li-desc')}>
                      Le nombre d'articles, de services, à faire et faits se recalcule pour la sélection.
                    </div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Multi-boutiques</div>
                    <div className={cx('feature-li-desc')}>
                      Les commandes de toutes vos boutiques partenaires, réunies et lisibles d'un coup d'œil.
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <div className={cx('reveal')}>
              <div className={cx('mock')}>
                <div className={cx('mock-head')}>
                  <div className={cx('mock-head-title')}>À remettre le 18/06/2026</div>
                  <div className={cx('mock-pill')}>Filtré par jour</div>
                </div>
                <div className={styles['preview-stats-row']} style={{ gap: 1, background: 'var(--gray-200)' }}>
                  <div style={{ background: 'white', padding: '16px 12px' }}>
                    <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>111</div>
                    <div style={{ fontSize: '0.66rem', color: 'var(--gray-400)' }}>Articles</div>
                  </div>
                  <div style={{ background: 'white', padding: '16px 12px' }}>
                    <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>127</div>
                    <div style={{ fontSize: '0.66rem', color: 'var(--gray-400)' }}>Services</div>
                  </div>
                  <div style={{ background: 'white', padding: '16px 12px' }}>
                    <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--amber)' }}>118</div>
                    <div style={{ fontSize: '0.66rem', color: 'var(--gray-400)' }}>À faire</div>
                  </div>
                  <div style={{ background: 'white', padding: '16px 12px' }}>
                    <div style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--green)' }}>9</div>
                    <div style={{ fontSize: '0.66rem', color: 'var(--gray-400)' }}>Faits</div>
                  </div>
                </div>
                <div className={cx('mock-row')}>
                  <div className={cx('mock-row-ic')}>🧥</div>
                  <div>
                    <div className={cx('mock-row-name')}>Veste / Parka — Father Marseille</div>
                    <div className={cx('mock-row-sub')}>Réf : Bec · Divers</div>
                  </div>
                  <span className={cx('mock-row-tag', 'tag-amber')}>À faire</span>
                </div>
                <div className={cx('mock-row')}>
                  <div className={cx('mock-row-ic')}>👗</div>
                  <div>
                    <div className={cx('mock-row-name')}>Jupe / Robe — Lulli Cv</div>
                    <div className={cx('mock-row-sub')}>Réf : Luciani · Ourlet piqué 2 piqûres</div>
                  </div>
                  <span className={cx('mock-row-tag', 'tag-amber')}>À faire</span>
                </div>
                <div className={cx('mock-row')}>
                  <div className={cx('mock-row-ic')}>👖</div>
                  <div>
                    <div className={cx('mock-row-name')}>Pantalon — Bianco Nero Marseille</div>
                    <div className={cx('mock-row-sub')}>Réf : Gandolfi · Ourlet piqué</div>
                  </div>
                  <span className={cx('mock-row-tag', 'tag-amber')}>À faire</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FEATURE: FICHE COMMANDE / PARCOURS ============ */}
        <section className={cx('feature-block', 'alt')}>
          <div className={cx('feature-inner', 'reverse')}>
            <div className={cx('feature-text', 'reveal')}>
              <span className={cx('section-eyebrow')}>Suivi de commande</span>
              <h2 className={cx('section-title')}>
                Le parcours complet,
                <br />
                sur une seule fiche.
              </h2>
              <p className={cx('section-sub')}>
                Chaque commande affiche son parcours horodaté, son paiement, son SMS, et le détail de chaque article
                avec les instructions de retouche et la personne qui a réalisé puis vérifié le travail.
              </p>
              <ul className={cx('feature-list')}>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Parcours visuel</div>
                    <div className={cx('feature-li-desc')}>
                      Collecté, travaillé, vérifié, livré — chaque étape avec auteur et horodatage.
                    </div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Paiement attribué</div>
                    <div className={cx('feature-li-desc')}>
                      Montant, mode de règlement et personne qui a encaissé, conservés sur la fiche.
                    </div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Détail par article</div>
                    <div className={cx('feature-li-desc')}>
                      Instructions précises, statut de vérification, terminé et vérifié par.
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <div className={cx('reveal')}>
              <div className={cx('mock')}>
                <div className={cx('mock-head')}>
                  <div>
                    <div className={cx('mock-head-title')}>Commande #4387</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)', marginTop: 2 }}>
                      Boogi France · créée par Anthony
                    </div>
                  </div>
                  <div className={cx('mock-pill', 'green')}>Payé</div>
                </div>
                <div className={cx('parcours')}>
                  <div className={cx('parcours-step')}>
                    <div className={cx('parcours-check')}>✓</div>
                    <div>
                      <div className={cx('parcours-title')}>Collectée chez la boutique</div>
                      <div className={cx('parcours-meta')}>12/06/2026 17:23 — Anthony</div>
                    </div>
                  </div>
                  <div className={cx('parcours-step')}>
                    <div className={cx('parcours-check')}>✓</div>
                    <div>
                      <div className={cx('parcours-title')}>Travail terminé en atelier</div>
                      <div className={cx('parcours-meta')}>16/06/2026 13:34 — Cyril</div>
                    </div>
                  </div>
                  <div className={cx('parcours-step')}>
                    <div className={cx('parcours-check')}>✓</div>
                    <div>
                      <div className={cx('parcours-title')}>Livrée à la société</div>
                      <div className={cx('parcours-meta')}>16/06/2026 14:37 — Anthony</div>
                    </div>
                  </div>
                </div>
                <div className={cx('mock-row')} style={{ borderTop: '1px solid var(--gray-200)' }}>
                  <div className={cx('mock-row-ic')}>🧥</div>
                  <div>
                    <div className={cx('mock-row-name')}>Longueur de manche boutonnières</div>
                    <div className={cx('mock-row-sub')}>Veste / Parka · Réf : Aroutunian</div>
                  </div>
                  <span className={cx('mock-row-tag', 'tag-green')}>Vérifié</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FEATURE: ENVOI SMS ============ */}
        <section className={cx('feature-block')}>
          <div className={cx('feature-inner')}>
            <div className={cx('feature-text', 'reveal')}>
              <span className={cx('section-eyebrow')}>Notifications</span>
              <h2 className={cx('section-title')}>
                Prévenez vos clients
                <br />
                en un clic.
              </h2>
              <p className={cx('section-sub')}>
                Dès qu'un article est prêt, le client — ou la boutique — apparaît dans la file d'envoi. Sélectionnez,
                envoyez en masse, et passez à la suite. Le message est personnalisable avec vos propres variables.
              </p>
              <ul className={cx('feature-list')}>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Envoi groupé</div>
                    <div className={cx('feature-li-desc')}>
                      Notifiez tous les clients prêts d'un seul geste, plus de relances manuelles.
                    </div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>SMS à variables</div>
                    <div className={cx('feature-li-desc')}>
                      {'{nom_client}, {numero_commande}, {nom_boutique} — avec aperçu en temps réel.'}
                    </div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Avis Google intégré</div>
                    <div className={cx('feature-li-desc')}>
                      Ajoutez votre lien d'avis pour transformer chaque retouche en recommandation.
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <div className={cx('reveal')}>
              <div className={cx('mock')}>
                <div className={cx('mock-head')}>
                  <div className={cx('mock-head-title')}>Envoi SMS</div>
                  <div className={cx('mock-pill', 'green')}>Envoyer (5)</div>
                </div>
                {[
                  { name: 'Huck H.', phone: '06 86 28 68 52' },
                  { name: 'Hadj B.', phone: '07 67 44 11 73' },
                  { name: 'Yasser A.', phone: '07 66 54 70 60' },
                  { name: 'Brigitte L.', phone: '06 28 04 05 03' },
                ].map((client) => (
                  <div key={client.phone} className={cx('mock-sms-row')}>
                    <div className={cx('mock-check')}>✓</div>
                    <div>
                      <div className={cx('mock-sms-name')}>{client.name}</div>
                      <div className={cx('mock-sms-phone')}>{client.phone}</div>
                    </div>
                    <span className={cx('mock-pill', 'blue')}>Prêt</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============ FEATURE: CATALOGUE DOUBLE PRIX ============ */}
        <section className={cx('feature-block', 'alt')}>
          <div className={cx('feature-inner', 'reverse')}>
            <div className={cx('feature-text', 'reveal')}>
              <span className={cx('section-eyebrow')}>Catalogue</span>
              <h2 className={cx('section-title')}>
                Un tarif comptoir,
                <br />
                un tarif société.
              </h2>
              <p className={cx('section-sub')}>
                Chaque prestation porte nativement deux prix : le tarif particulier au comptoir et le tarif négocié pour
                vos boutiques partenaires. Appliqué automatiquement selon le client, sans calcul manuel.
              </p>
              <ul className={cx('feature-list')}>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Double tarification</div>
                    <div className={cx('feature-li-desc')}>Prix B2C et prix société côte à côte, pour chaque service.</div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Organisé par article</div>
                    <div className={cx('feature-li-desc')}>
                      Chemise, pantalon, veste… chaque type avec ses prestations et son croquis.
                    </div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Modifiable à la volée</div>
                    <div className={cx('feature-li-desc')}>
                      Ajoutez un service, réordonnez, ajustez les prix quand vous voulez.
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <div className={cx('reveal')}>
              <div className={cx('mock')}>
                <div className={cx('mock-head')}>
                  <div className={cx('mock-head-title')}>Catalogue — Chemise</div>
                  <div className={cx('mock-pill')}>15 services</div>
                </div>
                <div className={cx('mock-cat-row', 'header')}>
                  <div className={cx('mock-cat-svc')}>Service</div>
                  <div className={cx('mock-cat-price')}>Prix</div>
                  <div className={cx('mock-cat-price')}>Société</div>
                </div>
                {[
                  { svc: 'Ampleur de manche court', price: '7,00 €', b2b: '6,00 €' },
                  { svc: 'Longueur de manche', price: '24,00 €', b2b: '22,00 €' },
                  { svc: 'Cintrage', price: '22,00 €', b2b: '15,00 €' },
                  { svc: 'Remonter épaule', price: '27,00 €', b2b: '26,00 €' },
                  { svc: 'Créer bouton + boutonnière', price: '13,00 €', b2b: '8,00 €' },
                ].map((row) => (
                  <div key={row.svc} className={cx('mock-cat-row')}>
                    <div className={cx('mock-cat-svc')}>{row.svc}</div>
                    <div className={cx('mock-cat-price')}>{row.price}</div>
                    <div className={cx('mock-cat-price', 'b2b')}>{row.b2b}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============ FEATURE: FINANCE (Z + RECAP) ============ */}
        <section className={cx('feature-block')}>
          <div className={cx('feature-inner')}>
            <div className={cx('feature-text', 'reveal')}>
              <span className={cx('section-eyebrow')}>Finance</span>
              <h2 className={cx('section-title')}>
                La caisse et la facturation,
                <br />
                déjà prêtes.
              </h2>
              <p className={cx('section-sub')}>
                Le Z de caisse clôture la journée par opérateur et par mode de règlement. Le récapitulatif société
                regroupe le mois entier d'une boutique, exportable en PDF ou envoyé par email en un clic.
              </p>
              <ul className={cx('feature-list')}>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Z de caisse</div>
                    <div className={cx('feature-li-desc')}>
                      Total par opérateur, ventilation espèces / carte, détail transaction par transaction.
                    </div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Récapitulatif mensuel B2B</div>
                    <div className={cx('feature-li-desc')}>
                      Toutes les retouches d'une société, avec instructions et références, total automatique.
                    </div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Facturation électronique</div>
                    <div className={cx('feature-li-desc')}>
                      PDF généré, envoyé par email — la fin des fins de mois approximatifs.
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <div className={cx('reveal')}>
              <div className={cx('mock')} style={{ marginBottom: 20 }}>
                <div className={cx('mock-head')}>
                  <div className={cx('mock-head-title')}>Z de Caisse — 17 juin 2026</div>
                  <div className={cx('mock-pill', 'blue')}>Imprimer</div>
                </div>
                <div className={cx('mock-cat-row', 'header')}>
                  <div className={cx('mock-cat-svc')}>Opérateur</div>
                  <div className={cx('mock-cat-price')}>Espèces</div>
                  <div className={cx('mock-cat-price')}>Carte</div>
                </div>
                {[
                  { name: 'Cyril', count: '13 cmd', cash: '287,50 €', card: '145,00 €' },
                  { name: 'Sarah', count: '9 cmd', cash: '196,00 €', card: '78,00 €' },
                  { name: 'Mohamed', count: '4 cmd', cash: '62,00 €', card: '0,00 €' },
                ].map((row) => (
                  <div key={row.name} className={cx('mock-cat-row')}>
                    <div className={cx('mock-cat-svc')}>
                      {row.name} <span className={cx('mock-cat-svc-muted')}>· {row.count}</span>
                    </div>
                    <div className={cx('mock-cat-price')}>{row.cash}</div>
                    <div className={cx('mock-cat-price')}>{row.card}</div>
                  </div>
                ))}
                <div className={cx('mock-total')}>
                  <div className={cx('mock-total-label')}>Total journée · 26 commandes</div>
                  <div className={cx('mock-total-amount')}>768,50 €</div>
                </div>
              </div>
              <div className={cx('mock')}>
                <div className={cx('mock-head')}>
                  <div className={cx('mock-head-title')}>Récap. — Agnès B Cmc · juin 2026</div>
                  <div className={cx('mock-pill', 'blue')}>PDF</div>
                </div>
                <div className={cx('mock-cat-row', 'header')}>
                  <div className={cx('mock-cat-svc')}>Article · Service</div>
                  <div className={cx('mock-cat-price')}>Réf</div>
                  <div className={cx('mock-cat-price')}>Montant</div>
                </div>
                {[
                  { svc: 'Pantalon · Ourlet piqué', ref: '0625', amount: '8,00 €' },
                  { svc: 'Haut · Manche 2 piqûres', ref: '0627', amount: '16,00 €' },
                  { svc: 'Veste · Manche boutonnières', ref: '0629', amount: '25,00 €' },
                  { svc: 'Jupe · Ourlet 2 piqûres', ref: '0637', amount: '15,00 €' },
                ].map((row) => (
                  <div key={row.ref} className={cx('mock-cat-row')}>
                    <div className={cx('mock-cat-svc')}>{row.svc}</div>
                    <div className={cx('mock-cat-price', 'mock-cat-ref')}>{row.ref}</div>
                    <div className={cx('mock-cat-price')}>{row.amount}</div>
                  </div>
                ))}
                <div className={cx('mock-total')}>
                  <div className={cx('mock-total-label')}>Total — juin 2026</div>
                  <div className={cx('mock-total-amount')}>149,50 €</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FEATURE: IMPRESSION TICKETS ============ */}
        <section className={cx('feature-block', 'alt')}>
          <div className={cx('feature-inner')}>
            <div className={cx('feature-text', 'reveal')}>
              <span className={cx('section-eyebrow')}>Impression</span>
              <h2 className={cx('section-title')}>
                Le ticket qui suit
                <br />
                chaque vêtement.
              </h2>
              <p className={cx('section-sub')}>
                Au dépôt, le client repart avec son bon. Chaque article reçoit son étiquette avec QR code de
                traçabilité et croquis technique, pour qu'aucune instruction ne se perde entre le comptoir et l'atelier.
              </p>
              <ul className={cx('feature-list')}>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Bon de dépôt client</div>
                    <div className={cx('feature-li-desc')}>
                      En-tête atelier, détail des prestations, total et message personnalisé en bas de ticket.
                    </div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>QR code de traçabilité</div>
                    <div className={cx('feature-li-desc')}>
                      Un scan rappelle la commande complète — fini les confusions d'articles en atelier.
                    </div>
                  </div>
                </li>
                <li>
                  <span className={cx('feature-check')}>✓</span>
                  <div>
                    <div className={cx('feature-li-title')}>Croquis technique</div>
                    <div className={cx('feature-li-desc')}>
                      Le schéma de la pièce imprimé sur l'étiquette, avec l'instruction et la date de remise.
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <div className={cx('reveal', 'tickets-row')}>
              <div className={cx('ticket')}>
                <div className={cx('ticket-logo')}>Josy Couture</div>
                <div className={cx('ticket-sub')}>
                  30 Rue de la Palaud · 13001 Marseille
                  <br />
                  Tél : 09 8111 0044 · www.josycouture.com
                </div>
                <div className={cx('ticket-h1')}>BON DE DÉPOSE</div>
                <div className={cx('ticket-center')}>
                  Commande #4625
                  <br />
                  <strong>Nicole R.</strong>
                </div>
                <div className={cx('ticket-dash')} />
                <div className={cx('ticket-line')}>
                  <span>Date dépose</span>
                  <span>17/06/2026</span>
                </div>
                <div className={cx('ticket-line')}>
                  <span>Livraison est.</span>
                  <span>30/06/2026</span>
                </div>
                <div className={cx('ticket-dash')} />
                <div className={cx('ticket-item')}>
                  <strong>Jeans (1/3)</strong>
                </div>
                <div className={cx('ticket-line')}>
                  <span>Formule côtés + OBR</span>
                  <span>25,00</span>
                </div>
                <div className={cx('ticket-item')}>
                  <strong>Jeans (2/3)</strong>
                </div>
                <div className={cx('ticket-line')}>
                  <span>Formule côtés + OBR</span>
                  <span>25,00</span>
                </div>
                <div className={cx('ticket-item')}>
                  <strong>Jeans (3/3)</strong>
                </div>
                <div className={cx('ticket-line')}>
                  <span>RAS</span>
                  <span>0,00</span>
                </div>
                <div className={cx('ticket-dash')} />
                <div className={cx('ticket-total')}>TOTAL : 50,00 €</div>
                <div className={cx('ticket-dash')} />
                <div className={cx('ticket-foot')}>
                  Vous recevrez un SMS dès l'achèvement du travail. 15 jours pour toute réclamation.
                </div>
              </div>
              <div className={cx('ticket', 'ticket-label')}>
                <div className={cx('ticket-center')} style={{ marginTop: 0 }}>
                  Commande #4625
                  <br />
                  <strong>Nicole R.</strong>
                </div>
                <div className={cx('ticket-dash')} />
                <div className={cx('ticket-center')}>
                  <strong>JEANS (1/3)</strong>
                  <br />
                  Formule côtés + OBR
                  <br />
                  <span className={cx('ticket-muted')}>Au model</span>
                </div>
                <div className={cx('ticket-qr')}>
                  <svg viewBox="0 0 100 100" width="86" height="86" aria-hidden="true">
                    <rect width="100" height="100" fill="white" />
                    <g fill="#0f0f14">
                      <rect x="4" y="4" width="26" height="26" />
                      <rect x="10" y="10" width="14" height="14" fill="white" />
                      <rect x="14" y="14" width="6" height="6" fill="#0f0f14" />
                      <rect x="70" y="4" width="26" height="26" />
                      <rect x="76" y="10" width="14" height="14" fill="white" />
                      <rect x="80" y="14" width="6" height="6" fill="#0f0f14" />
                      <rect x="4" y="70" width="26" height="26" />
                      <rect x="10" y="76" width="14" height="14" fill="white" />
                      <rect x="14" y="80" width="6" height="6" fill="#0f0f14" />
                      <rect x="38" y="6" width="6" height="6" />
                      <rect x="50" y="6" width="6" height="6" />
                      <rect x="38" y="18" width="6" height="6" />
                      <rect x="58" y="14" width="6" height="6" />
                      <rect x="38" y="38" width="6" height="6" />
                      <rect x="50" y="44" width="6" height="6" />
                      <rect x="62" y="38" width="6" height="6" />
                      <rect x="44" y="56" width="6" height="6" />
                      <rect x="56" y="56" width="6" height="6" />
                      <rect x="70" y="44" width="6" height="6" />
                      <rect x="82" y="50" width="6" height="6" />
                      <rect x="70" y="62" width="6" height="6" />
                      <rect x="38" y="70" width="6" height="6" />
                      <rect x="50" y="76" width="6" height="6" />
                      <rect x="62" y="82" width="6" height="6" />
                      <rect x="82" y="70" width="6" height="6" />
                      <rect x="76" y="82" width="6" height="6" />
                      <rect x="88" y="88" width="6" height="6" />
                      <rect x="44" y="88" width="6" height="6" />
                    </g>
                  </svg>
                </div>
                <div className={cx('ticket-dash')} />
                <div className={cx('ticket-sketch')}>👖</div>
                <div className={cx('ticket-dash')} />
                <div className={cx('ticket-center')}>
                  <strong>lundi 29 juin</strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ ROLES ============ */}
        <section className={styles.roles} id="roles">
          <div className={cx('roles-inner')}>
            <div className={cx('roles-header', 'reveal')}>
              <span className={cx('section-eyebrow')}>Comptes & rôles</span>
              <h2 className={cx('section-title')}>
                Chaque métier,
                <br />
                sa propre interface.
              </h2>
              <p className={cx('section-sub')}>
                Créez des comptes adaptés à chaque rôle de votre équipe. Chacun ne voit que ce dont il a besoin —
                l'accueil encaisse, le couturier produit, le coursier livre.
              </p>
            </div>
            <div className={cx('roles-grid')}>
              {ROLES.map((role) => (
                <div key={role.title} className={cx('role-card', 'reveal')}>
                  <div className={cx('role-card-head')}>
                    <div className={cx('role-icon', role.iconClass)}>{role.icon}</div>
                    <div className={cx('role-title')}>{role.title}</div>
                    <div className={cx('role-desc')}>{role.desc}</div>
                  </div>
                  <div className={cx('role-card-foot')}>
                    <div className={cx('role-access')}>Son interface</div>
                    {role.tags.map((tag) => (
                      <div key={tag} className={cx('role-tag')}>
                        {tag}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ CONFIG ============ */}
        <section className={styles.config}>
          <div className={cx('config-inner')}>
            <div className={cx('config-header', 'reveal')}>
              <span className={cx('section-eyebrow')}>Configuration</span>
              <h2 className={cx('section-title')}>Paramétré à votre atelier.</h2>
              <p className={cx('section-sub')}>
                Les détails qui font la différence au quotidien, réglables une fois et appliqués partout.
              </p>
            </div>
            <div className={cx('config-grid')}>
              <div className={cx('config-card', 'reveal')}>
                <div className={cx('config-card-ic')}>📅</div>
                <div>
                  <div className={cx('config-card-title')}>Délais & jours de travail</div>
                  <div className={cx('config-card-desc')}>
                    Définissez votre délai de livraison par défaut. Seamflow calcule la date de remise en sautant
                    automatiquement vos jours de fermeture.
                  </div>
                </div>
              </div>
              <div className={cx('config-card', 'reveal')}>
                <div className={cx('config-card-ic')}>💬</div>
                <div>
                  <div className={cx('config-card-title')}>SMS personnalisable</div>
                  <div className={cx('config-card-desc')}>
                    Rédigez votre message avec des variables dynamiques et visualisez l'aperçu en direct.
                  </div>
                  <div className={cx('config-sms-preview')}>
                    Bonjour <span className={cx('var')}>{'{nom_client}'}</span>,{' '}
                    <span className={cx('var')}>{'{nom_boutique}'}</span> vous informe que votre retouche{' '}
                    <span className={cx('var')}>{'{numero_commande}'}</span> est prête à être récupérée.
                  </div>
                </div>
              </div>
              <div className={cx('config-card', 'reveal')}>
                <div className={cx('config-card-ic')}>🖨️</div>
                <div>
                  <div className={cx('config-card-title')}>Impression CloudPRNT</div>
                  <div className={cx('config-card-desc')}>
                    L'imprimante récupère les tickets automatiquement via le serveur. Le message de bas de ticket client
                    se modifie en un clic et s'applique instantanément à toutes les impressions.
                  </div>
                </div>
              </div>
              <div className={cx('config-card', 'reveal')}>
                <div className={cx('config-card-ic')}>🔐</div>
                <div>
                  <div className={cx('config-card-title')}>Autorisations fines</div>
                  <div className={cx('config-card-desc')}>
                    Attribuez à chaque membre de l'équipe exactement les accès dont il a besoin, et rien de plus.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ TESTIMONIALS ============ */}
        <section className={styles.testimonials}>
          <div className={cx('testimonials-inner')}>
            <div className={cx('testi-header', 'reveal')}>
              <span className={cx('section-eyebrow')}>Témoignages</span>
              <h2 className={cx('section-title')}>Ce qu'ils en disent</h2>
            </div>
            <div className={cx('testi-grid')}>
              {TESTIMONIALS.map((testimonial) => (
                <div key={testimonial.name} className={cx('testi-card', 'reveal')}>
                  <div className={cx('testi-stars')}>★★★★★</div>
                  <p className={cx('testi-text')}>“{testimonial.text}”</p>
                  <div className={cx('testi-author')}>
                    <div className={cx('testi-av')}>{testimonial.initials}</div>
                    <div>
                      <div className={cx('testi-name')}>{testimonial.name}</div>
                      <div className={cx('testi-role')}>{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ PRICING ============ */}
        <section id="tarif">
          <div className={cx('pricing')}>
            <div className={cx('pricing-header', 'reveal')}>
              <span className={cx('section-eyebrow')}>Tarif</span>
              <h2 className={cx('section-title')}>Un tarif pour chaque métier.</h2>
              <p className={cx('section-sub')}>
                Que vous soyez l'atelier qui réalise les retouches ou la boutique qui les confie, Seamflow a une offre
                claire et sans frais cachés.
              </p>
            </div>
            <div className={cx('price-grid')}>
              {PRICE_PLANS.map((plan) => (
                <div key={plan.type} className={cx('price-card', plan.alt ? 'price-card-alt' : '', 'reveal')}>
                  <div className={cx('price-card-type')}>{plan.type}</div>
                  <div className={cx('price-badge-top', plan.alt ? 'alt' : '')}>{plan.badge}</div>
                  <div className={cx('price-num')}>
                    <sup>€</sup>
                    {plan.price}
                  </div>
                  <div className={cx('price-period')}>{plan.period}</div>
                  <ul className={cx('price-list', 'price-list-single')}>
                    {plan.features.map((feature) => (
                      <li key={feature}>
                        <span className={cx('price-check')}>✓</span> {feature}
                      </li>
                    ))}
                  </ul>
                  <div className={cx('price-highlights')}>
                    {plan.highlights.map((highlight) => (
                      <div key={highlight.title} className={cx('price-highlight')}>
                        <div className={cx('price-highlight-ic')}>{highlight.icon}</div>
                        <div className={cx('price-highlight-body')}>
                          <div className={cx('price-highlight-title')}>{highlight.title}</div>
                          <div className={cx('price-highlight-desc')}>{highlight.desc}</div>
                        </div>
                        <div className={cx('price-highlight-tag')}>{highlight.tag}</div>
                      </div>
                    ))}
                  </div>
                  <a
                    className={cx(plan.alt ? 'btn-outline-light' : 'btn-white')}
                    href={whatsAppLink(DEMO_MESSAGE)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Réserver une démo gratuite →
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ CTA ============ */}
        <section className={styles['cta-final']}>
          <span className={cx('section-eyebrow', 'reveal')}>Prêt à commencer ?</span>
          <h2 className={cx('section-title', 'reveal')}>
            Reprenez le contrôle
            <br />
            de votre atelier.
          </h2>
          <p className={cx('section-sub', 'reveal')}>
            Démonstration personnalisée en 30 minutes. Prise en main immédiate, aucune installation requise.
          </p>
          <div className={cx('cta-actions', 'reveal')}>
            <a className={cx('btn-primary')} href={whatsAppLink(DEMO_MESSAGE)} target="_blank" rel="noopener noreferrer">
              Réserver une démo →
            </a>
            <a
              className={cx('btn-outline')}
              href={whatsAppLink(CONTACT_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Nous contacter
            </a>
          </div>
        </section>

        {/* ============ FORMATION OPCO ============ */}
        <section className={styles.opco} id="formation">
          <div className={cx('opco-inner')}>
            <div className={cx('reveal')}>
              <span className={cx('section-eyebrow')}>Formation</span>
              <h2 className={cx('section-title')}>
                Formez votre équipe,
                <br />
                sans avancer les frais.
              </h2>
              <p className={cx('section-sub')}>
                La prise en main de Seamflow peut être financée par votre OPCO. Nous vous accompagnons dans le montage
                du dossier pour une formation prise en charge, adaptée à votre atelier et à chaque rôle.
              </p>
              <div className={cx('opco-badge-row')}>
                <span className={cx('opco-badge')}>Accompagnement dossier</span>
                <span className={cx('opco-badge')}>Sur site ou à distance</span>
                <span className={cx('opco-badge')}>Par rôle métier</span>
              </div>
            </div>
            <div className={cx('reveal')}>
              <div className={cx('opco-card')}>
                <div className={cx('opco-card-amount')}>Jusqu'à 100%</div>
                <div className={cx('opco-card-amount-sub')}>de la formation prise en charge par votre OPCO</div>
                <ul className={cx('opco-card-list')}>
                  <li>
                    <span className={cx('opco-card-check')}>✓</span> Montage du dossier de financement
                  </li>
                  <li>
                    <span className={cx('opco-card-check')}>✓</span> Formation adaptée accueil, couturier, coursier
                  </li>
                  <li>
                    <span className={cx('opco-card-check')}>✓</span> Prise en main complète de la plateforme
                  </li>
                  <li>
                    <span className={cx('opco-card-check')}>✓</span> Support après formation
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FOOTER ============ */}
        <footer className={styles.footer}>
          <div className={cx('footer-logo')}>Seamflow</div>
          <ul className={cx('footer-links')}>
            <li>
              <a href="#fonctions">Fonctionnalités</a>
            </li>
            <li>
              <a href="#tarif">Tarif</a>
            </li>
            <li>
              <a href={whatsAppLink(CONTACT_MESSAGE)} target="_blank" rel="noopener noreferrer">
                Contact
              </a>
            </li>
          </ul>
          <div className={cx('footer-copy')}>© {new Date().getFullYear()} Seamflow · SIREN 993 869 924</div>
        </footer>
      </div>
    </>
  )
}

// Opt out of default Layout — this page renders its own chrome
LandingPage.layout = (page: React.ReactNode) => page
